import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useNotifications } from '@toolpad/core';
import { useForm, useWatch } from 'react-hook-form';
import {
    CheckboxElement,
    FormContainer,
    SelectElement,
    TextFieldElement,
} from 'react-hook-form-mui';
import { Card, CardContent, CardHeader, Stack } from '@mui/material';

import {
    CategoryInput,
    EditButtons,
    PourFormatInput,
    ProductInput,
    ProductLineInput,
    ProductSubtypeInput,
    ProductTypeInput,
    StandardDrinkInput,
} from '@barboxx/admin-components';
import { POUR_AVAILABILITY_SCHEMA } from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import { PourAvailability } from '@barboxx/models';
import { Can, useBool } from '@barboxx/react-utils';
import { POUR_AVAILABILITY_ENTITY_TYPE } from '@barboxx/types';
import { enumToFormOptions } from '@barboxx/utils';

interface Props {
    pourAvailability: PourAvailability.DetailsResponse;
}

export const PourAvailabilityDetailsCard = ({ pourAvailability }: Props) => {
    const [editable, editableActions] = useBool(false);
    const notifications = useNotifications();

    const mutatePourAvailability = useMutation({
        mutationKey: ['pourAvailability', pourAvailability.id],
        mutationFn: (data: PourAvailability.UpdateRequest) =>
            Api.Menu.PourAvailability.putPourAvailability(
                pourAvailability.id,
                data
            ),
    });

    const formContext = useForm<PourAvailability.UpdateRequest>({
        resolver: yupResolver(POUR_AVAILABILITY_SCHEMA),
        defaultValues: pourAvailability,
    });

    const handleCancel = () => {
        editableActions.off();
        formContext.reset(undefined);
    };

    const onSubmit = async (values: PourAvailability.UpdateRequest) => {
        mutatePourAvailability
            .mutateAsync(values)
            .then(({ data }) => {
                notifications.show('Updated menu item!', {
                    severity: 'success',
                });

                editableActions.off();
                formContext.reset(data);
            })
            .catch(e => {
                notifications.show('Error Updating menu item!', {
                    severity: 'error',
                });
            });
    };

    const values = useWatch(formContext) as PourAvailability.UpdateRequest;

    return (
        <Card>
            <CardHeader title="PourAvailability Details" />
            <CardContent>
                <FormContainer onSuccess={onSubmit} formContext={formContext}>
                    <Stack gap={2}>
                        <PourFormatInput disabled={!editable} />
                        <SelectElement
                            label="Entity Type"
                            name="entityType"
                            options={enumToFormOptions(
                                POUR_AVAILABILITY_ENTITY_TYPE
                            )}
                            disabled={!editable}
                            onChange={() => {
                                formContext.resetField('productTypeId');
                                formContext.resetField('productSubtypeId');
                                formContext.resetField('categoryId');
                                formContext.resetField('productId');
                                formContext.resetField('productLineId');
                                formContext.resetField('standardDrinkId');
                            }}
                            fullWidth
                        />
                        {values.entityType ===
                            POUR_AVAILABILITY_ENTITY_TYPE.Drink && (
                            <StandardDrinkInput disabled={!editable} />
                        )}
                        {values.entityType ===
                            POUR_AVAILABILITY_ENTITY_TYPE.Product && (
                            <ProductInput disabled={!editable} />
                        )}
                        {values.entityType ===
                            POUR_AVAILABILITY_ENTITY_TYPE.ProductLine && (
                            <ProductLineInput disabled={!editable} />
                        )}
                        {values.entityType ===
                            POUR_AVAILABILITY_ENTITY_TYPE.ProductType && (
                            <ProductTypeInput disabled={!editable} />
                        )}
                        {values.entityType ===
                            POUR_AVAILABILITY_ENTITY_TYPE.ProductSubtype && (
                            <ProductSubtypeInput disabled={!editable} />
                        )}
                        {values.entityType ===
                            POUR_AVAILABILITY_ENTITY_TYPE.Category && (
                            <CategoryInput disabled={!editable} />
                        )}
                        <TextFieldElement
                            label="displayOrder"
                            name="displayOrder"
                            fullWidth
                            disabled={!editable}
                        />

                        <CheckboxElement
                            label="Default"
                            name="isDefault"
                            disabled={!editable}
                        />

                        <Can I="update" a="PourAvailability">
                            <EditButtons
                                formContext={formContext}
                                handleCancel={handleCancel}
                                editable={editable}
                                editableActions={editableActions}
                            />
                        </Can>
                    </Stack>
                </FormContainer>
            </CardContent>
        </Card>
    );
};
